import React, { useState, useEffect, useRef } from 'react';

const SYMBOLS = [
  { symbol: '^DJI',  label: 'DOW',    fmt: 'index' },
  { symbol: '^GSPC', label: 'S&P',    fmt: 'index' },
  { symbol: '^IXIC', label: 'NASDAQ', fmt: 'index' },
  { symbol: '^TNX',  label: '10-YR',  fmt: 'yield' },
  { symbol: '^VIX',  label: 'VIX',    fmt: 'plain' },
];

const fmtNum = (n, decimals = 2) => {
  if (n == null || isNaN(n)) return '—';
  return Number(n).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const isMarketOpen = () => {
  const now = new Date();
  const et = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const day = et.getDay();
  if (day === 0 || day === 6) return false;
  const mins = et.getHours() * 60 + et.getMinutes();
  return mins >= 570 && mins < 960; // 9:30 AM – 4:00 PM ET
};

const TickerItem = ({ data }) => {
  if (!data) return null;
  const { label, price, change, changePct, fmt } = data;
  const up = change >= 0;
  const color = up ? '#4caf50' : '#ef5350';
  const arrow = up ? '▲' : '▼';

  let priceStr;
  if (fmt === 'yield') {
    priceStr = fmtNum(price, 3) + '%';
  } else {
    priceStr = fmtNum(price);
  }

  let changeStr;
  if (fmt === 'yield') {
    changeStr = `${up ? '+' : ''}${fmtNum(change, 3)}`;
  } else {
    changeStr = `${up ? '+' : ''}${fmtNum(change)} (${up ? '+' : ''}${fmtNum(changePct)}%)`;
  }

  return (
    <span style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
      <span style={{ color: '#a0b0b8', fontWeight: 600, fontSize: '11px', letterSpacing: '0.5px' }}>{label}</span>
      <span style={{ color: '#e8e8e8', fontWeight: 700, fontSize: '12px' }}>{priceStr}</span>
      <span style={{ color, fontWeight: 600, fontSize: '11px' }}>{arrow} {changeStr}</span>
    </span>
  );
};

const SEP = (
  <span style={{ color: '#3a4a54', margin: '0 16px', fontSize: '10px', userSelect: 'none' }}>•</span>
);

const FinancialTicker = ({ inline = false }) => {
  const [quotes, setQuotes] = useState(null);
  const [marketOpen, setMarketOpen] = useState(isMarketOpen());
  const contentRef = useRef(null);
  const [contentWidth, setContentWidth] = useState(0);

  const fetchQuotes = async () => {
    try {
      const res = await fetch('/api/ticker');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setQuotes(data.quotes || null);
      setMarketOpen(data.market_open ?? isMarketOpen());
    } catch {
      // Keep existing data on error; only null on first load failure
    }
  };

  useEffect(() => {
    fetchQuotes();
    const interval = setInterval(fetchQuotes, 60000);
    return () => clearInterval(interval);
  }, []);

  // Measure content width for seamless scroll
  useEffect(() => {
    if (contentRef.current) {
      setContentWidth(contentRef.current.scrollWidth);
    }
  }, [quotes]);

  const renderItems = () => {
    if (!quotes) {
      return (
        <span style={{ color: '#a0b0b8', fontSize: '11px' }}>
          Loading market data...
        </span>
      );
    }
    return SYMBOLS.map((s, i) => {
      const q = quotes[s.symbol];
      return (
        <React.Fragment key={s.symbol}>
          {i > 0 && SEP}
          <TickerItem data={q ? { label: s.label, fmt: s.fmt, ...q } : { label: s.label, price: null, change: 0, changePct: 0, fmt: s.fmt }} />
        </React.Fragment>
      );
    });
  };

  const closedBadge = !marketOpen && quotes ? (
    <span style={{
      color: '#1a252f', background: '#a0b0b8', fontSize: '9px', fontWeight: 700,
      padding: '1px 5px', borderRadius: '3px', marginLeft: '14px', letterSpacing: '0.5px',
      userSelect: 'none',
    }}>
      CLOSED
    </span>
  ) : null;

  // Animation: scroll the duplicated content strip
  const animDuration = Math.max(contentWidth / 40, 20); // ~40px/sec

  const scrollContent = (
    <>
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: '100%',
        animation: quotes ? `tickerScroll ${animDuration}s linear infinite` : 'none',
        whiteSpace: 'nowrap',
      }}>
        <div ref={contentRef} style={{ display: 'inline-flex', alignItems: 'center', padding: '0 24px' }}>
          {renderItems()}
          {closedBadge}
        </div>
        {quotes && (
          <div style={{ display: 'inline-flex', alignItems: 'center', padding: '0 24px' }}>
            {renderItems()}
            {closedBadge}
          </div>
        )}
      </div>
    </>
  );

  if (inline) {
    return (
      <div style={{ flex: 1, overflow: 'hidden', height: '100%', position: 'relative', marginLeft: '12px' }}>
        {scrollContent}
      </div>
    );
  }

  return (
    <div style={{
      height: '28px',
      background: '#1a252f',
      borderBottom: '1px solid #253340',
      overflow: 'hidden',
      position: 'relative',
      flexShrink: 0,
    }}>
      {scrollContent}
    </div>
  );
};

export default FinancialTicker;
